import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Snackbar,
  Paper,
  IconButton,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { saidaService } from '../../services/saida';
import { romaneioService } from '../../services/romaneio';
import { vendedorService, Vendedor } from '../../services/vendedor';
import { produtoService, Produto } from '../../services/produto';
import DatePickerField from '../common/DatePickerField';

interface ProdutoSaida {
  id: number;
  idProduto: number;
  referencia: string;
  nomeProduto: string;
  precoVenda: number;
  semComissao: boolean;
  quantidade: number;
}

interface RomaneioSelectItem {
  id: number;
  idPraca: number;
  praca: string;
  idFuncionario: number;
  funcionario: string;
  data: string;
  dataString: string;
}

const SaidaForm: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Estado do formulário
  const [selectedRomaneio, setSelectedRomaneio] = useState<RomaneioSelectItem | null>(null);
  const [funcionario, setFuncionario] = useState('');
  const [codFuncionario, setCodFuncionario] = useState('');
  const [codPraca, setCodPraca] = useState('');
  const [praca, setPraca] = useState('');
  const [selectedVendedor, setSelectedVendedor] = useState<Vendedor | null>(null);
  const [dataSaida, setDataSaida] = useState('');
  const [dataRetorno, setDataRetorno] = useState('');
  const [bloqueado, setBloqueado] = useState(true);

  // Estado dos produtos
  const [produtos, setProdutos] = useState<ProdutoSaida[]>([]);

  // Estado de erros e mensagens
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' });
  const [produtosDialogOpen, setProdutosDialogOpen] = useState(false);
  const [produtosParaAdicionar, setProdutosParaAdicionar] = useState<any[]>([]);

  const filterProdutos = createFilterOptions({
    stringify: (option: any) => `${option.referencia || ''} ${option.nomeProduto || option.nome || ''}`.toLowerCase(),
    limit: 50,
  });

  // Buscar romaneios para select
  const { data: romaneiosData = [] } = useQuery({
    queryKey: ['romaneios', 'select'],
    queryFn: romaneioService.getRomaneioSelect,
  });

  // Buscar vendedores por praça (quando romaneio selecionado)
  const { data: vendedoresData = [] } = useQuery({
    queryKey: ['vendedores', 'praca', codPraca],
    queryFn: () => vendedorService.getVendedoresByPraca(Number(codPraca)),
    enabled: !!codPraca,
  });

  // Buscar produtos do romaneio selecionado
  const { data: produtosDisponiveis = [] } = useQuery({
    queryKey: ['produtos', 'romaneio', selectedRomaneio?.id],
    queryFn: () => produtoService.getForSelectByRomaneio(Number(selectedRomaneio!.id)),
    enabled: !!selectedRomaneio?.id,
  });

  // Quando seleciona um romaneio
  const handleRomaneioChange = (romaneio: RomaneioSelectItem | null) => {
    setSelectedRomaneio(romaneio);
    if (romaneio) {
      setCodPraca(String(romaneio.idPraca));
      setCodFuncionario(String(romaneio.idFuncionario));
      setFuncionario(romaneio.funcionario || '');
      // Limpar vendedor e produtos ao trocar romaneio
      setSelectedVendedor(null);
      setProdutos([]);
      setBloqueado(true);
    } else {
      setCodPraca('');
      setCodFuncionario('');
      setFuncionario('');
      setPraca('');
      setSelectedVendedor(null);
      setProdutos([]);
      setBloqueado(true);
    }
  };

  // Quando seleciona um vendedor
  const handleVendedorChange = (vendedor: Vendedor | null) => {
    setSelectedVendedor(vendedor);
    if (vendedor) {
      const situacao = Number(vendedor.situacao);
      if (situacao !== 1) {
        setBloqueado(true);
        if (situacao === 2) {
          setSnackbar({
            open: true,
            message: 'Vendedor bloqueado por falta de pagamento, consulte o valor da dívida no cadastro ou entre em contato com a empresa.',
            severity: 'error',
          });
        } else if (situacao === 3) {
          setSnackbar({
            open: true,
            message: 'Vendedor bloqueado por falta de devolução de mercadoria, entre em contato com a empresa.',
            severity: 'error',
          });
        } else if (situacao === 4) {
          setSnackbar({
            open: true,
            message: 'Vendedor bloqueado por CPF negativado no SPC, entre em contato com a empresa.',
            severity: 'error',
          });
        }
      } else {
        setPraca(vendedor.praca || '');
        setBloqueado(false);
      }
    } else {
      setBloqueado(true);
      setPraca('');
    }
  };

  const openProdutosDialog = () => {
    setProdutosParaAdicionar([]);
    setProdutosDialogOpen(true);
  };

  const closeProdutosDialog = () => {
    setProdutosDialogOpen(false);
    setProdutosParaAdicionar([]);
  };

  const handleAdicionarProdutos = () => {
    const novosProdutos: ProdutoSaida[] = produtosParaAdicionar.map((p: any) => ({
      id: 0,
      idProduto: getProdutoId(p),
      referencia: p.referencia || '',
      nomeProduto: p.nomeProduto || p.nome || '',
      precoVenda: Number(p.precoVenda) || 0,
      semComissao: p.semComissao === 1 || p.semComissao === true,
      quantidade: 1,
    }));
    setProdutos((prev) => [...prev, ...novosProdutos]);
    setProdutosParaAdicionar([]);
    setProdutosDialogOpen(false);
  };

  // Remover produto da tabela
  const removeProduto = (index: number) => {
    setProdutos(produtos.filter((_, i) => i !== index));
  };

  // Helper para obter o ID do produto independente do campo retornado pela API
  const getProdutoId = (produto: any): number => {
    return Number(produto.idProduto ?? produto.id ?? 0);
  };

  // Selecionar produto no dropdown
  const handleProdutoChange = (index: number, idProduto: number) => {
    const produto: any = produtosDisponiveis.find((p: any) => getProdutoId(p) === Number(idProduto));
    if (produto) {
      const newProdutos = [...produtos];
      newProdutos[index] = {
        ...newProdutos[index],
        idProduto: getProdutoId(produto),
        nomeProduto: produto.nomeProduto || produto.nome || '',
        referencia: produto.referencia || '',
        precoVenda: Number(produto.precoVenda) || 0,
        semComissao: produto.semComissao === 1 || produto.semComissao === true,
      };
      setProdutos(newProdutos);
    }
  };

  // Atualizar campo do produto
  const updateProduto = (index: number, field: keyof ProdutoSaida, value: any) => {
    const newProdutos = [...produtos];
    newProdutos[index] = { ...newProdutos[index], [field]: value };
    setProdutos(newProdutos);
  };

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: async () => {
      const notaSaida = {
        IdRomaneio: selectedRomaneio?.id || '',
        IdVendedor: selectedVendedor?.id?.toString() || '',
        DataSaida: dataSaida ? convertToApiFormat(dataSaida) : '',
        DataRetorno: dataRetorno ? convertToApiFormat(dataRetorno) : '',
        Produtos: produtos.map((prod) => ({
          id: prod.id !== undefined ? prod.id : 0,
          idProduto: prod.idProduto,
          quantidade: prod.quantidade,
          precoVenda: prod.precoVenda,
          semComissao: prod.semComissao ? 1 : 0,
        })),
      };
      return saidaService.salvar(notaSaida as any);
    },
    onSuccess: (data: any) => {
      // Se retornar array, é erro da API
      if (Array.isArray(data)) {
        setSnackbar({
          open: true,
          message: data[0]?.title + ' - ' + data[0]?.detail,
          severity: 'error',
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['saidas'] });
      navigate('/saidas');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar saída:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar uma nova saída: ' + (error.message || error),
        severity: 'error',
      });
    },
  });

  // Converte dd/mm/yyyy para yyyy-mm-dd
  const convertToApiFormat = (dateString: string): string => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  };

  // Validação
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!selectedRomaneio) {
      newErrors.romaneio = 'Romaneio é obrigatório';
    }
    if (!selectedVendedor) {
      newErrors.vendedor = 'Vendedor é obrigatório';
    }
    if (!dataSaida) {
      newErrors.dataSaida = 'Data de saída é obrigatória';
    }
    if (!dataRetorno) {
      newErrors.dataRetorno = 'Data de retorno é obrigatória';
    }
    if (produtos.length === 0) {
      newErrors.produtos = 'Não é possível salvar uma saída sem produtos vinculados.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submeter formulário
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      if (errors.produtos) {
        setSnackbar({ open: true, message: errors.produtos, severity: 'error' });
      }
      return;
    }
    saveMutation.mutate();
  };

  const handleBack = () => {
    navigate('/saidas');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Nova Saída
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {/* Romaneio */}
            <Box sx={{ flex: '1 1 100%' }}>
              <Autocomplete
                options={romaneiosData}
                getOptionLabel={(option: any) => `${option.dataString} - ${option.praca} - ${option.funcionario}`}
                value={selectedRomaneio as any}
                onChange={(_, value) => handleRomaneioChange(value as RomaneioSelectItem)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Romaneio"
                    placeholder="Selecione..."
                    error={!!errors.romaneio}
                    helperText={errors.romaneio}
                  />
                )}
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              />
            </Box>

            {/* Funcionário (readonly) */}
            <Box sx={{ flex: '1 1 100%' }}>
              <TextField
                fullWidth
                label="Funcionário"
                value={funcionario}
                slotProps={{
                  input: { readOnly: true },
                }}
              />
            </Box>

            {/* Vendedor */}
            <Box sx={{ flex: '1 1 100%' }}>
              <Autocomplete
                options={vendedoresData}
                getOptionLabel={(option) => {
                  const v = option as any;
                  return v.textoExibir || v.nome || '';
                }}
                value={selectedVendedor}
                onChange={(_, value) => handleVendedorChange(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendedor(a)"
                    placeholder="Selecione..."
                    error={!!errors.vendedor}
                    helperText={errors.vendedor}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={!codPraca}
              />
            </Box>

            {/* Data Saída, Data Retorno e Praça */}
            <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
              <DatePickerField
                label="Data Saída"
                value={dataSaida}
                onChange={(value) => setDataSaida(value)}
                error={!!errors.dataSaida}
                helperText={errors.dataSaida}
              />
            </Box>

            <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
              <DatePickerField
                label="Data Retorno"
                value={dataRetorno}
                onChange={(value) => setDataRetorno(value)}
                error={!!errors.dataRetorno}
                helperText={errors.dataRetorno}
              />
            </Box>

            <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
              <TextField
                fullWidth
                label="Praça"
                value={praca}
                slotProps={{
                  input: { readOnly: true },
                }}
              />
            </Box>

            {/* Produtos */}
            <Box sx={{ flex: '1 1 100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Produtos
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openProdutosDialog}
                  disabled={!selectedRomaneio}
                >
                  Adicionar Produto
                </Button>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Preço de Venda</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Sem Comissão</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {produtos.map((produto, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={produto.idProduto || 0}
                              onChange={(e) => handleProdutoChange(index, Number(e.target.value))}
                              displayEmpty
                              renderValue={(selected: any) => {
                                if (!selected || selected === 0) return 'Selecione um produto';
                                const selectedProduct: any = produtosDisponiveis.find((p: any) => getProdutoId(p) === Number(selected));
                                const displayText = selectedProduct?.informacoesExibir || selectedProduct?.nomeProduto || selectedProduct?.nome || produto.nomeProduto || 'Selecione um produto';
                                return displayText;
                              }}
                              disabled={!selectedRomaneio}
                            >
                              <MenuItem value={0}>Selecione um produto</MenuItem>
                              {produtosDisponiveis.map((prod: any) => (
                                <MenuItem key={getProdutoId(prod)} value={getProdutoId(prod)}>
                                  {prod.informacoesExibir || prod.nomeProduto || prod.nome || 'Produto sem nome'}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>{produto.referencia}</TableCell>
                        <TableCell>{formatCurrency(produto.precoVenda)}</TableCell>
                        <TableCell>{produto.semComissao ? 'SIM' : 'NÃO'}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={produto.quantidade}
                            onChange={(e) => updateProduto(index, 'quantidade', Number(e.target.value))}
                            sx={{ width: 80 }}
                            slotProps={{
                              htmlInput: { min: 1 },
                            }}
                            disabled={produto.id !== 0}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => removeProduto(index)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {produtos.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography sx={{ py: 4, color: 'text.secondary' }}>
                            Nenhum produto adicionado
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>

          {/* Erro geral */}
          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="error">{errors.submit}</Alert>
            </Box>
          )}

          {/* Botões de ação */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="outlined" onClick={handleBack} disabled={saveMutation.isPending}>
              Voltar
            </Button>
            {!bloqueado && (
              <Button
                variant="contained"
                type="submit"
                startIcon={<SaveIcon />}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            )}
          </Box>
        </form>
      </Paper>

      <Dialog open={produtosDialogOpen} onClose={closeProdutosDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Produtos</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            fullWidth
            options={produtosDisponiveis}
            getOptionLabel={(option: any) => option.informacoesExibir || option.nomeProduto || option.nome || 'Produto sem nome'}
            value={produtosParaAdicionar}
            onChange={(_, value) => setProdutosParaAdicionar(value)}
            isOptionEqualToValue={(option: any, value: any) => getProdutoId(option) === getProdutoId(value)}
            filterOptions={filterProdutos}
            disabled={produtosDisponiveis.length === 0}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Selecione os produtos"
                placeholder="Pesquise ou selecione..."
              />
            )}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProdutosDialog} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleAdicionarProdutos}
            variant="contained"
            disabled={produtosParaAdicionar.length === 0}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SaidaForm;
